import { type ReactNode, useState } from 'react';
import { TableSkeleton } from './Skeleton';
import { useCsvExport } from '../hooks/useCsvExport';
import { useXlsxExport } from '../hooks/useXlsxExport';

interface DataTableProps<T> {
  columns: { key: string; header: string; render?: (row: T) => ReactNode; sortable?: boolean; filterable?: boolean; help?: string; width?: string; align?: 'left' | 'right' | 'center' }[];
  data: T[];
  loading?: boolean;
  onRowClick?: (row: T) => void;
  emptyMessage?: string;
  sortKey?: string;
  sortDir?: 'asc' | 'desc';
  onSort?: (key: string) => void;
  filters?: Record<string, string>;
  onFilterChange?: (key: string, value: string) => void;
  /** Page title used for the CSV/XLSX export filename (e.g. "Compras" → "Compras-2026-06-19"). Falls back to "datos". */
  title?: string;
  /** Enable XLSX export button (default: true). Gracefully hidden if exceljs fails to load. */
  enableXlsxExport?: boolean;
}

export function DataTable<T extends Record<string, any>>({
  columns, data, loading = false, onRowClick, emptyMessage = 'Sin datos',
  sortKey, sortDir, onSort, filters, onFilterChange, title, enableXlsxExport = true,
}: DataTableProps<T>) {
  const csvFilename = title || 'datos';
  const { exportCsv } = useCsvExport(columns, data, csvFilename);
  const { exportXlsx } = useXlsxExport();
  const [xlsxLoading, setXlsxLoading] = useState(false);
  const [xlsxAvailable, setXlsxAvailable] = useState(true);
  const exportDisabled = loading || data.length === 0;
  // Fixed layout squeezes every column into the container width, so it's only safe when every column declares
  // its width. Otherwise columns size to their content and the wrapper scrolls horizontally.
  const fixedLayout = columns.every((c) => c.width);

  const handleXlsxExport = async () => {
    if (!enableXlsxExport || !xlsxAvailable) return;
    setXlsxLoading(true);
    try {
      await exportXlsx(
        columns.map(c => ({ key: c.key, header: c.header })),
        data,
        csvFilename,
      );
    } catch {
      setXlsxAvailable(false);
    } finally {
      setXlsxLoading(false);
    }
  };

  if (loading) return <TableSkeleton rows={5} cols={columns.length} />;

  const sortIcon = (col: string) => {
    if (col !== sortKey) return <span className="ml-1 text-slate-300">⇅</span>;
    return sortDir === 'asc' ? <span className="ml-1 text-brand-600">▲</span> : <span className="ml-1 text-brand-600">▼</span>;
  };

  const cellValue = (col: (typeof columns)[number], row: T): ReactNode =>
    col.render ? col.render(row) : String(row[col.key] ?? '');

  const emptyState = (
    <>
      <svg className="w-12 h-12 mx-auto mb-3 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" /></svg>
      {emptyMessage}
    </>
  );

  return (
    <div>
      <div className="flex justify-end mb-2 gap-2">
        <button
          onClick={exportCsv}
          disabled={exportDisabled}
          title="Exportar a CSV"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-brand-50 hover:border-brand-300 hover:text-brand-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:border-slate-200 disabled:hover:text-slate-600"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          Exportar CSV
        </button>
        {enableXlsxExport && xlsxAvailable && (
          <button
            onClick={handleXlsxExport}
            disabled={exportDisabled || xlsxLoading}
            title={xlsxLoading ? 'Generando XLSX...' : 'Exportar a XLSX'}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-brand-50 hover:border-brand-300 hover:text-brand-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:border-slate-200 disabled:hover:text-slate-600"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Exportar XLSX
          </button>
          )}
        </div>

      {/* Mobile-only sort/filter controls (below md:) — the card list below has no header row to tap-to-sort/filter on. */}
      {(onSort || onFilterChange) && (
        <div className="md:hidden mb-3 space-y-2">
          {onSort && (
            <select
              value={sortKey ?? ''}
              onChange={(e) => e.target.value && onSort(e.target.value)}
              aria-label="Ordenar por"
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:border-brand-400"
            >
              <option value="">Ordenar por…</option>
              {columns.filter((c) => c.sortable !== false).map((c) => (
                <option key={c.key} value={c.key}>
                  {c.header}{sortKey === c.key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                </option>
              ))}
            </select>
          )}
          {onFilterChange && (
            <div className="space-y-2">
              {columns.filter((c) => c.filterable !== false).map((c) => (
                <input
                  key={`mf-${c.key}`}
                  value={filters?.[c.key] ?? ''}
                  onChange={(e) => onFilterChange(c.key, e.target.value)}
                  placeholder={`Filtrar por ${c.header}...`}
                  aria-label={`Filtrar por ${c.header}`}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:border-brand-400"
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Desktop/tablet table (md: and up) — unchanged */}
      <div className="hidden md:block overflow-x-auto rounded-lg border border-slate-200 bg-white" role="table" aria-label="Tabla de datos">
        <table className={`w-full text-sm ${fixedLayout ? 'table-fixed' : ''}`}>
          {fixedLayout && (
            <colgroup>
              {columns.map((col) => (
                <col key={col.key} style={{ width: col.width }} />
              ))}
            </colgroup>
          )}
          <thead>
            <tr className="border-b border-slate-200" role="row">
              {columns.map((col) => (
                <th key={col.key}
                  className={`px-4 py-3 font-semibold text-slate-600 whitespace-nowrap ${col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left'} ${col.sortable !== false && onSort ? 'cursor-pointer hover:bg-slate-100 select-none' : ''}`}
                  role="columnheader" scope="col"
                  title={col.help || ''}
                  onClick={() => col.sortable !== false && onSort?.(col.key)}>
                  {col.header}{col.sortable !== false && onSort ? sortIcon(col.key) : ''}
                </th>
              ))}
            </tr>
            {onFilterChange && (
              <tr className="border-b border-slate-100 bg-slate-50" role="row">
                {columns.map((col) => (
                  <th key={`f-${col.key}`} className="px-2 py-1" role="columnheader" scope="col">
                    {col.filterable !== false ? (
                      <input
                        value={filters?.[col.key] ?? ''}
                        onChange={e => onFilterChange(col.key, e.target.value)}
                        placeholder="Filtrar..."
                        aria-label={`Filtrar por ${col.header}`}
                        className="w-full px-2 py-1 text-xs border border-slate-200 rounded bg-white focus:outline-none focus:border-brand-400"
                      />
                    ) : <span />}
                  </th>
                ))}
              </tr>
            )}
          </thead>
          <tbody>
            {data.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="text-center py-12 text-slate-400" role="status">
                  {emptyState}
                </td>
              </tr>
            ) : (
              data.map((row, idx) => (
                <tr key={idx} className={`border-b border-slate-100 hover:bg-brand-50 transition-colors ${onRowClick ? 'cursor-pointer' : ''}`}
                  onClick={() => onRowClick?.(row)} role="row" tabIndex={onRowClick ? 0 : undefined}
                  onKeyDown={onRowClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onRowClick(row); } } : undefined}
                  aria-label={onRowClick ? `Ver detalle` : undefined}>
                  {columns.map((col) => (
                    <td key={col.key} className={`px-4 py-2.5 ${col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : ''} ${col.width ? 'overflow-hidden' : 'whitespace-nowrap'}`}>
                      {col.width && !fixedLayout ? <div style={{ width: col.width }}>{cellValue(col, row)}</div> : cellValue(col, row)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile card list (below md:) — one card per row, columns stacked as label:value pairs. */}
      <div className="md:hidden">
        {data.length === 0 ? (
          <div className="text-center py-12 text-slate-400 bg-white rounded-lg border border-slate-200" role="status">
            {emptyState}
          </div>
        ) : (
          <div className="space-y-2">
            {data.map((row, idx) => (
              <div
                key={idx}
                className={`rounded-lg border border-slate-200 bg-white px-4 py-3 transition-colors ${onRowClick ? 'cursor-pointer active:bg-brand-50' : ''}`}
                onClick={() => onRowClick?.(row)}
                role={onRowClick ? 'button' : undefined}
                tabIndex={onRowClick ? 0 : undefined}
                onKeyDown={onRowClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onRowClick(row); } } : undefined}
                aria-label={onRowClick ? 'Ver detalle' : undefined}
              >
                {columns.map((col) => (
                  <div key={col.key} className="flex items-start justify-between gap-3 py-1 border-b border-slate-50 last:border-b-0">
                    <span className="text-xs font-medium text-slate-400 uppercase tracking-wide shrink-0 pt-0.5">{col.header}</span>
                    <span className="text-slate-700 text-sm text-right min-w-0 break-words">{cellValue(col, row)}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
